import supabase from './supabase';

/**
 * Google Business Profile Service
 * Handles review syncing, AI reply generation, and reply posting
 */
class GoogleBusinessService {
  /**
   * Trigger manual sync of Google Business reviews
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Sync result
   */
  async syncReviews(organizationId) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-sync-reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ organizationId }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync reviews');
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error syncing reviews:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to sync reviews'
      };
    }
  }

  /**
   * Generate AI reply for a review
   * @param {string} reviewId - Review ID
   * @param {string} organizationId - Organization ID
   * @param {boolean} regenerate - Force regenerate even if reply exists
   * @returns {Promise<Object>} AI-generated reply
   */
  async generateAIReply(reviewId, organizationId, regenerate = false) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-generate-ai-reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ reviewId, organizationId, regenerate }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate reply');
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error generating AI reply:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to generate AI reply'
      };
    }
  }

  /**
   * Post reply to Google Business Profile
   * @param {string} reviewId - Review ID
   * @param {string} organizationId - Organization ID
   * @param {string} replyText - Reply text to post
   * @returns {Promise<Object>} Post result
   */
  async postReply(reviewId, organizationId, replyText) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/google-post-reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ reviewId, organizationId, replyText }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to post reply');
      }

      return { success: true, ...data };
    } catch (error) {
      console.error('Error posting reply:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to post reply'
      };
    }
  }

  /**
   * Get reply settings for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Reply settings
   */
  async getReplySettings(organizationId) {
    try {
      const { data, error } = await supabase
        .from('google_reply_settings')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return { 
          success: true, 
          data: {
            auto_reply_enabled: false,
            auto_reply_for_ratings: [4, 5],
            reply_tone: 'professional',
            require_approval: true,
            custom_instructions: null,
            business_context: null,
          }
        };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error getting reply settings:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get reply settings',
        data: null
      };
    }
  }

  /**
   * Update reply settings for an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} Update result
   */
  async updateReplySettings(organizationId, settings) {
    try {
      const { data, error } = await supabase
        .from('google_reply_settings')
        .upsert({
          organization_id: organizationId,
          ...settings,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'organization_id'
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating reply settings:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to update reply settings'
      };
    }
  }

  /**
   * Get Google reviews for an organization
   * @param {string} organizationId - Organization ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object>} Reviews
   */
  async getGoogleReviews(organizationId, filters = {}) {
    try {
      let query = supabase
        .from('reviews')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('review_source', 'google_business')
        .eq('is_archived', false)
        .order('submitted_at', { ascending: false });

      // Apply filters
      if (filters.rating) {
        query = query.eq('rating', filters.rating);
      }
      if (filters.hasReply !== undefined) {
        if (filters.hasReply) {
          query = query.not('platform_response_text', 'is', null);
        } else {
          query = query.is('platform_response_text', null);
        }
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error getting Google reviews:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get reviews',
        data: []
      };
    }
  }

  /**
   * Get reviews pending reply approval
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Pending reviews
   */
  async getPendingReplies(organizationId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('review_source', 'google_business')
        .eq('reply_status', 'pending_approval')
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error getting pending replies:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get pending replies',
        data: []
      };
    }
  }

  /**
   * Approve AI-generated reply
   * @param {string} reviewId - Review ID
   * @returns {Promise<Object>} Update result
   */
  async approveReply(reviewId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .update({ 
          reply_status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error approving reply:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to approve reply'
      };
    }
  }

  /**
   * Get statistics for Google reviews
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Statistics
   */
  async getGoogleReviewStats(organizationId) {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('rating, platform_response_text')
        .eq('organization_id', organizationId)
        .eq('review_source', 'google_business')
        .eq('is_archived', false);

      if (error) throw error;

      const total = data.length;
      const withReplies = data.filter(r => r.platform_response_text).length;
      const averageRating = total > 0 
        ? data.reduce((sum, r) => sum + r.rating, 0) / total 
        : 0;

      const ratingDistribution = {
        1: data.filter(r => r.rating === 1).length,
        2: data.filter(r => r.rating === 2).length,
        3: data.filter(r => r.rating === 3).length,
        4: data.filter(r => r.rating === 4).length,
        5: data.filter(r => r.rating === 5).length,
      };

      return { 
        success: true, 
        stats: {
          total,
          withReplies,
          withoutReplies: total - withReplies,
          averageRating: averageRating.toFixed(1),
          ratingDistribution,
        }
      };
    } catch (error) {
      console.error('Error getting Google review stats:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to get stats'
      };
    }
  }
}

export default new GoogleBusinessService();

